/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  FileCopy as CopyIcon,
  MoveUp as MoveUpIcon,
  MoveDown as MoveDownIcon,
  ViewHeadline as HeaderIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import TemplateEditor from './TemplateEditor';
import { v4 as uuidv4 } from 'uuid';

const PAGE_NUMBER_FORMATS = {
  numeric: (num) => num.toString(),
  roman: (num) => {
    const romanNumerals = [
      { value: 1000, numeral: 'M' },
      { value: 900, numeral: 'CM' },
      { value: 500, numeral: 'D' },
      { value: 400, numeral: 'CD' },
      { value: 100, numeral: 'C' },
      { value: 90, numeral: 'XC' },
      { value: 50, numeral: 'L' },
      { value: 40, numeral: 'XL' },
      { value: 10, numeral: 'X' },
      { value: 9, numeral: 'IX' },
      { value: 5, numeral: 'V' },
      { value: 4, numeral: 'IV' },
      { value: 1, numeral: 'I' }
    ];
    let result = '';
    let n = num;
    for (const { value, numeral } of romanNumerals) {
      while (n >= value) {
        result += numeral;
        n -= value;
      }
    }
    return result.toLowerCase();
  },
  alpha: (num) => {
    let result = '';
    let n = num - 1;
    while (n >= 0) {
      result = String.fromCharCode(97 + (n % 26)) + result;
      n = Math.floor(n / 26) - 1;
    }
    return result;
  }
};

// eslint-disable-next-line max-lines-per-function
export default function MultiPageEditor({
  template,
  onChange,
  variables = [],
  customVariables = [],
  onVariableInsert,
  _entityBranding
}) {
  const [pages, setPages] = useState([]);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [headerContent, setHeaderContent] = useState('');
  const [footerContent, setFooterContent] = useState('');
  const [pageSettings, setPageSettings] = useState({
    orientation: 'portrait',
    size: 'letter',
    margins: { top: 72, bottom: 72, left: 72, right: 72 },
    headerHeight: 36,
    footerHeight: 36,
    pageNumbering: {
      enabled: true,
      position: 'footer-center',
      format: 'numeric',
      startNumber: 1
    }
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [headerFooterOpen, setHeaderFooterOpen] = useState(false);
  const [pageMenuAnchor, setPageMenuAnchor] = useState(null);
  const [selectedPageId, setSelectedPageId] = useState(null);

  // Initialize pages from template
  useEffect(() => {
    if (template) {
      if (template.pages && template.pages.length > 0) {
        setPages(template.pages);
      } else if (template.htmlContent) {
        // Single page from htmlContent
        setPages([{
          id: uuidv4(),
          pageNumber: 1,
          content: template.htmlContent
        }]);
      } else {
        // Empty template
        setPages([{
          id: uuidv4(),
          pageNumber: 1,
          content: ''
        }]);
      }

      setHeaderContent(template.headerContent || '');
      setFooterContent(template.footerContent || '');
      if (template.pageSettings) {
        setPageSettings({ ...pageSettings, ...template.pageSettings });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  // Update parent when changes occur
  useEffect(() => {
    if (onChange) {
      onChange({
        pages,
        headerContent,
        footerContent,
        pageSettings,
        // Also provide combined htmlContent for backward compatibility
        htmlContent: pages.map(p => p.content).join('<div class="page-break"></div>')
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, headerContent, footerContent, pageSettings]);

  const handlePageChange = (pageIndex, content) => {
    const updatedPages = [...pages];
    updatedPages[pageIndex].content = content;
    setPages(updatedPages);
  };

  const addPage = (afterIndex = null) => {
    const newPage = {
      id: uuidv4(),
      pageNumber: pages.length + 1,
      content: ''
    };

    let updatedPages;
    if (afterIndex !== null) {
      updatedPages = [
        ...pages.slice(0, afterIndex + 1),
        newPage,
        ...pages.slice(afterIndex + 1)
      ];
    } else {
      updatedPages = [...pages, newPage];
    }

    // Renumber pages
    updatedPages.forEach((page, index) => {
      page.pageNumber = index + 1;
    });

    setPages(updatedPages);
    setActivePageIndex(afterIndex !== null ? afterIndex + 1 : updatedPages.length - 1);
  };

  const deletePage = (pageId) => {
    if (pages.length <= 1) {
      alert('Cannot delete the last page');
      return;
    }

    const pageIndex = pages.findIndex(p => p.id === pageId);
    const updatedPages = pages.filter(p => p.id !== pageId);

    // Renumber pages
    updatedPages.forEach((page, index) => {
      page.pageNumber = index + 1;
    });

    setPages(updatedPages);

    // Adjust active page if needed
    if (activePageIndex >= updatedPages.length) {
      setActivePageIndex(updatedPages.length - 1);
    } else if (activePageIndex > pageIndex) {
      setActivePageIndex(activePageIndex - 1);
    }
  };

  const movePage = (pageId, direction) => {
    const pageIndex = pages.findIndex(p => p.id === pageId);
    if (
      (direction === 'up' && pageIndex === 0) ||
      (direction === 'down' && pageIndex === pages.length - 1)
    ) {
      return;
    }

    const updatedPages = [...pages];
    const targetIndex = direction === 'up' ? pageIndex - 1 : pageIndex + 1;

    // Swap pages
    [updatedPages[pageIndex], updatedPages[targetIndex]] =
    [updatedPages[targetIndex], updatedPages[pageIndex]];

    // Renumber pages
    updatedPages.forEach((page, index) => {
      page.pageNumber = index + 1;
    });

    setPages(updatedPages);
    setActivePageIndex(targetIndex);
  };

  const duplicatePage = (pageId) => {
    const pageIndex = pages.findIndex(p => p.id === pageId);
    const pageToDuplicate = pages[pageIndex];

    const newPage = {
      ...pageToDuplicate,
      id: uuidv4(),
      pageNumber: pageIndex + 2
    };

    const updatedPages = [
      ...pages.slice(0, pageIndex + 1),
      newPage,
      ...pages.slice(pageIndex + 1)
    ];

    // Renumber pages
    updatedPages.forEach((page, index) => {
      page.pageNumber = index + 1;
    });

    setPages(updatedPages);
    setActivePageIndex(pageIndex + 1);
  };

  const updatePageSettings = (newSettings) => {
    setPageSettings({ ...pageSettings, ...newSettings });
    setSettingsOpen(false);
  };

  const renderPageTab = (page) => (
    <Tab
      key={page.id}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Page {page.pageNumber}</Typography>
          {page.customHeader && <Chip label="H" size="small" />}
          {page.customFooter && <Chip label="F" size="small" />}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPageId(page.id);
              setPageMenuAnchor(e.currentTarget);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    />
  );

  const renderPageMenu = () => (
    <Menu
      anchorEl={pageMenuAnchor}
      open={Boolean(pageMenuAnchor)}
      onClose={() => setPageMenuAnchor(null)}
    >
      <MenuItem onClick={() => {
        const pageIndex = pages.findIndex(p => p.id === selectedPageId);
        addPage(pageIndex);
        setPageMenuAnchor(null);
      }}>
        <AddIcon fontSize="small" sx={{ mr: 1 }} />
        Add Page After
      </MenuItem>
      <MenuItem onClick={() => {
        duplicatePage(selectedPageId);
        setPageMenuAnchor(null);
      }}>
        <CopyIcon fontSize="small" sx={{ mr: 1 }} />
        Duplicate Page
      </MenuItem>
      <MenuItem onClick={() => {
        movePage(selectedPageId, 'up');
        setPageMenuAnchor(null);
      }} disabled={pages.findIndex(p => p.id === selectedPageId) === 0}>
        <MoveUpIcon fontSize="small" sx={{ mr: 1 }} />
        Move Up
      </MenuItem>
      <MenuItem onClick={() => {
        movePage(selectedPageId, 'down');
        setPageMenuAnchor(null);
      }} disabled={pages.findIndex(p => p.id === selectedPageId) === pages.length - 1}>
        <MoveDownIcon fontSize="small" sx={{ mr: 1 }} />
        Move Down
      </MenuItem>
      <Divider />
      <MenuItem onClick={() => {
        if (window.confirm('Delete this page?')) {
          deletePage(selectedPageId);
        }
        setPageMenuAnchor(null);
      }} disabled={pages.length <= 1}>
        <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
        Delete Page
      </MenuItem>
    </Menu>
  );

  // eslint-disable-next-line max-lines-per-function
  const renderPageSettings = () => (
    <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Page Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Page Size</InputLabel>
            <Select
              value={pageSettings.size}
              onChange={(e) => setPageSettings({ ...pageSettings, size: e.target.value })}
              label="Page Size"
            >
              <MenuItem value="letter">Letter (8.5" x 11")</MenuItem>
              <MenuItem value="legal">Legal (8.5" x 14")</MenuItem>
              <MenuItem value="a4">A4 (210mm x 297mm)</MenuItem>
              <MenuItem value="a3">A3 (297mm x 420mm)</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Orientation</InputLabel>
            <Select
              value={pageSettings.orientation}
              onChange={(e) => setPageSettings({ ...pageSettings, orientation: e.target.value })}
              label="Orientation"
            >
              <MenuItem value="portrait">Portrait</MenuItem>
              <MenuItem value="landscape">Landscape</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="subtitle2">Margins (in points)</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Top"
              type="number"
              value={pageSettings.margins.top}
              onChange={(e) => setPageSettings({
                ...pageSettings,
                margins: { ...pageSettings.margins, top: parseInt(e.target.value) }
              })}
            />
            <TextField
              label="Bottom"
              type="number"
              value={pageSettings.margins.bottom}
              onChange={(e) => setPageSettings({
                ...pageSettings,
                margins: { ...pageSettings.margins, bottom: parseInt(e.target.value) }
              })}
            />
            <TextField
              label="Left"
              type="number"
              value={pageSettings.margins.left}
              onChange={(e) => setPageSettings({
                ...pageSettings,
                margins: { ...pageSettings.margins, left: parseInt(e.target.value) }
              })}
            />
            <TextField
              label="Right"
              type="number"
              value={pageSettings.margins.right}
              onChange={(e) => setPageSettings({
                ...pageSettings,
                margins: { ...pageSettings.margins, right: parseInt(e.target.value) }
              })}
            />
          </Box>

          <Divider />

          <FormControlLabel
            control={
              <Switch
                checked={pageSettings.pageNumbering?.enabled || false}
                onChange={(e) => setPageSettings({
                  ...pageSettings,
                  pageNumbering: {
                    ...pageSettings.pageNumbering,
                    enabled: e.target.checked
                  }
                })}
              />
            }
            label="Show Page Numbers"
          />

          {pageSettings.pageNumbering?.enabled && (
            <>
              <FormControl fullWidth>
                <InputLabel>Page Number Position</InputLabel>
                <Select
                  value={pageSettings.pageNumbering.position}
                  onChange={(e) => setPageSettings({
                    ...pageSettings,
                    pageNumbering: {
                      ...pageSettings.pageNumbering,
                      position: e.target.value
                    }
                  })}
                  label="Page Number Position"
                >
                  <MenuItem value="header-left">Header - Left</MenuItem>
                  <MenuItem value="header-center">Header - Center</MenuItem>
                  <MenuItem value="header-right">Header - Right</MenuItem>
                  <MenuItem value="footer-left">Footer - Left</MenuItem>
                  <MenuItem value="footer-center">Footer - Center</MenuItem>
                  <MenuItem value="footer-right">Footer - Right</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Number Format</InputLabel>
                <Select
                  value={pageSettings.pageNumbering.format}
                  onChange={(e) => setPageSettings({
                    ...pageSettings,
                    pageNumbering: {
                      ...pageSettings.pageNumbering,
                      format: e.target.value
                    }
                  })}
                  label="Number Format"
                >
                  <MenuItem value="numeric">1, 2, 3...</MenuItem>
                  <MenuItem value="roman">i, ii, iii...</MenuItem>
                  <MenuItem value="alpha">a, b, c...</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Start Number"
                type="number"
                value={pageSettings.pageNumbering.startNumber || 1}
                onChange={(e) => setPageSettings({
                  ...pageSettings,
                  pageNumbering: {
                    ...pageSettings.pageNumbering,
                    startNumber: parseInt(e.target.value)
                  }
                })}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
        <Button onClick={() => updatePageSettings(pageSettings)} variant="contained">
          Apply Settings
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderHeaderFooterDialog = () => (
    <Dialog
      open={headerFooterOpen}
      onClose={() => setHeaderFooterOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit Header and Footer</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Header
          </Typography>
          <TemplateEditor
            initialContent={headerContent}
            onChange={setHeaderContent}
            variables={[...variables, ...customVariables]}
            onVariableInsert={onVariableInsert}
            placeholder="Enter header content..."
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Footer
          </Typography>
          <TemplateEditor
            initialContent={footerContent}
            onChange={setFooterContent}
            variables={[...variables, ...customVariables]}
            onVariableInsert={onVariableInsert}
            placeholder="Enter footer content..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setHeaderFooterOpen(false)}>Cancel</Button>
        <Button onClick={() => setHeaderFooterOpen(false)} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Multi-Page Template Editor</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<HeaderIcon />}
            onClick={() => setHeaderFooterOpen(true)}
          >
            Header/Footer
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(true)}
          >
            Page Settings
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activePageIndex}
            onChange={(e, newValue) => setActivePageIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {pages.map((page, index) => renderPageTab(page, index))}
            <Tab
              icon={<AddIcon />}
              label="Add Page"
              onClick={() => addPage()}
              sx={{ minWidth: 'auto' }}
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          {pages[activePageIndex] && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Editing Page {pages[activePageIndex].pageNumber} of {pages.length}
                {pageSettings.pageNumbering?.enabled && (
                  <Typography variant="caption" display="block">
                    Page number will appear in {pageSettings.pageNumbering.position.replace('-', ' ')} as: {
                      PAGE_NUMBER_FORMATS[pageSettings.pageNumbering.format](
                        pages[activePageIndex].pageNumber + (pageSettings.pageNumbering.startNumber - 1)
                      )
                    }
                  </Typography>
                )}
              </Alert>

              <TemplateEditor
                key={pages[activePageIndex].id}
                initialContent={pages[activePageIndex].content}
                onChange={(content) => handlePageChange(activePageIndex, content)}
                variables={[...variables, ...customVariables]}
                onVariableInsert={onVariableInsert}
                placeholder={`Enter content for page ${pages[activePageIndex].pageNumber}...`}
              />
            </>
          )}
        </Box>
      </Paper>

      {renderPageMenu()}
      {renderPageSettings()}
      {renderHeaderFooterDialog()}
    </Box>
  );
}